import React from 'react';
import { Button, IconButton, Menu, MenuItem } from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import { logoutCleanup } from '@/auth';
import { type LaunchDarklyFlags } from '@/common/launchDarkly';
import { useEditEmailDialog } from '@/components/senderBuild/footer-controls/use-edit-email-dialog';
import { useSureYouWantToLeaveDialog } from '@/components/senderBuild/footer-controls/use-sure-you-want-to-leave-dialog';
import { useAnchorEl } from '@/hooks';
import { useNavigatedFromForms } from '@/hooks/use-navigated-from-forms';
import { useSearchParams } from '@/hooks/use-search-params';
import { FooterControls } from '../../components/senderBuild/footer-controls/footer-controls';
import { useShallowSelector } from '../../lib/reduxHooks';
import { withFrameOrigin } from '../../utils/frame-origin';
import { BuilderExitBackButtons } from './builder-exit-back-buttons';
import { shouldUseFormsRedirect } from './navigation-utils';

export function EnvelopeBuilderFooter(props: { flags: LaunchDarklyFlags; onOpenApplyTemplateDrawer: () => void }) {
  const { flags, onOpenApplyTemplateDrawer } = props;
  const { selectedDocumentId, blocks, saving, config } = useShallowSelector((state) => ({
    selectedDocumentId: state.senderBuild.currentDocument,
    blocks: state.senderBuild.blocks,
    saving: state.senderBuild.saving,
    config: state.senderBuild.config,
  }));

  const history = useHistory();
  const anchor = useAnchorEl();
  const editEmail = useEditEmailDialog({ flags });
  const shouldUseFormsNavigation = useNavigatedFromForms();
  const sureYouWantToLeave = useSureYouWantToLeaveDialog({ flags });
  const { theme } = useSearchParams();
  const isAgnosticTheme = theme === 'agnostic';

  function documentHasABlock() {
    for (const docId in blocks) {
      for (const pageNum in blocks[docId]) {
        if (blocks[docId][pageNum]?.some((b) => !b.isEditingDisabled)) {
          return true;
        }
      }
    }
    return false;
  }

  function handleFormsSaveAndExitClick() {
    sureYouWantToLeave.onNavigateAwayFromPage();
    logoutCleanup();

    const redirectUrl = config?.senderUi?.sendRedirectUrl;
    if (shouldUseFormsRedirect(redirectUrl)) {
      const formsRedirectUrl = new URL(redirectUrl);
      window.location.href = withFrameOrigin(formsRedirectUrl.origin + formsRedirectUrl.pathname);
    } else {
      window.history.back();
    }
  }

  return (
    <FooterControls>
      {/* Apply Template MOBILE button */}
      {flags?.enableEnvelopeBuilderTemplatePicker ? (
        <>
          <IconButton onClick={anchor.handleClick} disabled={!selectedDocumentId} className="lg:hidden">
            <MoreVert />
            <span className="sr-only">More</span>
          </IconButton>
          <Menu {...anchor.menuProps}>
            <MenuItem onClick={() => onOpenApplyTemplateDrawer()}>Apply Template</MenuItem>
          </Menu>
        </>
      ) : null}

      {/* If user came from Forms, this (usually) renders _nothing_; otherwise "<- Back" */}
      <BuilderExitBackButtons flags={flags} />

      <div className="lg:self-end flex items-center gap-4">
        {flags?.['hide-build-ui-elements'] || isAgnosticTheme ? null : shouldUseFormsNavigation ? (
          <Button
            color="primary"
            variant="outlined"
            onClick={handleFormsSaveAndExitClick}
            data-spec="formsSaveExitButton"
            className="max-md:hidden rounded-full py-3 px-8"
          >
            Save & Exit
          </Button>
        ) : (
          <Button
            color="primary"
            onClick={() => sureYouWantToLeave.showModal(() => history.push('/envelopes'))}
            disabled={saving}
            data-spec="exitButton"
            className="max-md:hidden rounded-full py-3 px-8 translate-x-2 hover:bg-blue-50"
          >
            Exit
          </Button>
        )}
        {sureYouWantToLeave.renderDialog()}

        {/* Apply Template DESKTOP button */}
        {flags?.enableEnvelopeBuilderTemplatePicker && !isAgnosticTheme ? (
          <Button
            color="primary"
            variant="outlined"
            className="max-lg:hidden"
            disabled={!selectedDocumentId}
            onClick={() => onOpenApplyTemplateDrawer()}
          >
            Apply Template
          </Button>
        ) : null}

        <Button
          data-spec="sendButton"
          variant="contained"
          color="primary"
          onClick={editEmail.toggleModal}
          disabled={saving || !documentHasABlock()}
          className="max-md:w-[140px] py-4 md:py-3 rounded-full"
        >
          Send
        </Button>
        {editEmail.renderDialog()}
      </div>
    </FooterControls>
  );
}
